/**
 * Analytics API Schemas
 *
 * Zod schemas for analytics endpoints:
 * - GET /api/v1/analytics/call-stats
 * - GET /api/v1/analytics/call-series-funnel
 */

import { z } from 'zod/v4';
import { dashboardFlowChannelSchema, projectIdsFilterSchema } from './common';

// ============================================================================
// Shared enums
// ============================================================================

export const analyticsPeriodSchema = z
  .enum(['today', 'week', 'month', 'quarter'])
  .default('week');

// ============================================================================
// GET /analytics/call-stats
// ============================================================================

export const callStatsQuerySchema = z.object({
  period: analyticsPeriodSchema,
  /** @deprecated legacy single-project filter — kept for one release cycle
   *  (SPA and edge-api deploy separately) so a stale bundle still filters.
   *  Ignored whenever `projectIds` is present and non-empty. */
  projectId: z.string().uuid().optional(),
  /** Multi-project filter (project-multiselect K5) — wins over `projectId`
   *  whenever present and non-empty; the two are never merged. */
  projectIds: projectIdsFilterSchema,
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  includeDemo: z.enum(['yes', 'no']).default('yes'),
  // Raw string, parsed + clamped route-side via parseTzOffset (WS1-T6) — kept
  // permissive here so garbage input falls back to UTC instead of a 400.
  tzOffset: z.string().optional(),
  /** `records.type` — narrows every part of the answer, as on `/dashboard/flow`.
   *  No channel — calls and chats; the old client dashboard sends none, so its
   *  numbers stay what they were. The call-statistics page sends `call`.
   *  The query is not `.strict()`, so an edge without the field ignores it. */
  channel: dashboardFlowChannelSchema.optional(),
});

export const callStatsDirectionItemSchema = z.object({
  direction: z.string(),
  count: z.number().int().nonnegative(),
});

export const callStatsHourItemSchema = z.object({
  hour: z.number().int().min(0).max(23),
  count: z.number().int().nonnegative(),
});

export const callStatsDurationBinItemSchema = z.object({
  bin: z.string(),
  count: z.number().int().nonnegative(),
});

export const callStatsAgentWorkTimeItemSchema = z.object({
  /**
   * The person's key (#1927): an Aelo user id, `<installation>:<id>` of a CRM
   * employee without an account, or the bare `agent_id`. Optional only so a UI
   * released before the edge-api keeps parsing a response from the older one.
   */
  key: z.string().optional(),
  agentId: z.string(),
  /** The installation of a CRM employee without an account; `null` otherwise. */
  installationId: z.string().nullable().optional(),
  agentName: z.string().nullable(),
  recordCount: z.number().int().nonnegative(),
  totalDurationSeconds: z.number().int().nonnegative(),
});

export const callStatsProjectItemSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  count: z.number().int().nonnegative(),
});

export const callStatsDailyTrendItemSchema = z.object({
  day: z.string(),
  count: z.number().int().nonnegative(),
});

/**
 * The flow's missed-call definitions (`missed-call-counts.ts` in
 * `@aica/database`): calls only, whatever `channel` is. `share` is
 * `inbound / (inboundCalls + inbound)` — the unknown direction is outside it,
 * the outbound ones («the client did not answer») are a number apart; `null`
 * when there were no inbound calls to divide by.
 */
export const callStatsMissedSchema = z.object({
  inbound: z.number().int().nonnegative(),
  outbound: z.number().int().nonnegative(),
  unknownDirection: z.number().int().nonnegative(),
  inboundCalls: z.number().int().nonnegative(),
  share: z.number().min(0).max(1).nullable(),
});

export const callStatsResponseSchema = z.object({
  period: analyticsPeriodSchema,
  total: z.number().int().nonnegative(),
  analyzedTotal: z.number().int().nonnegative(),
  avgDurationSeconds: z.number().nonnegative(),
  avgQualityScore: z.number().nullable(),
  byDirection: z.array(callStatsDirectionItemSchema),
  byHour: z.array(callStatsHourItemSchema),
  durationBins: z.array(callStatsDurationBinItemSchema),
  agentWorkTime: z.array(callStatsAgentWorkTimeItemSchema),
  byProject: z.array(callStatsProjectItemSchema),
  dailyTrend: z.array(callStatsDailyTrendItemSchema),
  comparison: z.object({
    prevTotal: z.number().int().nonnegative(),
    prevAvgDurationSeconds: z.number().nonnegative(),
    totalGrowthPercent: z.number().nullable(),
    durationChangePercent: z.number().nullable(),
  }),
  /** Optional for the rollback: the page deploys apart from the edge, and an
   *  edge without the block must read as «—», never as 0. */
  missed: callStatsMissedSchema.optional(),
});

// ============================================================================
// GET /analytics/missed-calls
// ============================================================================

/** The longest window `/missed-calls` counts: a year, with a leap day. */
export const MISSED_CALLS_MAX_WINDOW_DAYS = 366;

/**
 * The window is `periodStart`/`periodEnd` (half-open) or the rolling `period`,
 * as `call-stats`. The window is already exact, so nothing is bucketed by a
 * time-zone offset and the query carries none.
 */
export const missedCallsQuerySchema = z.object({
  period: analyticsPeriodSchema,
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  includeDemo: z.enum(['yes', 'no']).default('yes'),
  /** Projects of the report (the dashboard card's own choice, #2472) — applied
   *  next to the viewer's own boundary, which a named project cannot widen;
   *  absent — every project they may see. */
  projectIds: projectIdsFilterSchema,
});

export const missedCallsEmployeeSchema = z.object({
  /**
   * One person (#1927): the Aelo user id when the person's records sit under a
   * CRM id and/or their own user id; `<installation id>:<id>` for a CRM
   * employee with no account (the id is local to the portal); the bare
   * `agent_id` for a record with no installation. `null` — the records with no
   * employee: one row «не назначен». Never derived from a name. Not unique on
   * its own — a free-form bare id may spell an installation-scoped key — so a
   * row is (`installationId`, `key`). Optional only so that the page survives
   * an edge-api rolled back to the release without it: the client then keys
   * the row by `agentId`.
   */
  key: z.string().nullable().optional(),
  /** The id to show when there is no name: the user id, else the CRM id. */
  agentId: z.string().nullable(),
  /**
   * The CRM installation of a CRM employee with no account (their id is local
   * to the portal); `null` for a person tied to an Aelo user, a record with no
   * installation, and the «не назначен» row. Kept for the client of the
   * previous release, which still requires it while edge-api deploys first;
   * the row's identity is `key`. Removal: #2983.
   */
  installationId: z.string().nullable(),
  agentName: z.string().nullable(),
  inbound: z.number().int().nonnegative(),
  outbound: z.number().int().nonnegative(),
  unknownDirection: z.number().int().nonnegative(),
});

export const MISSED_CALLS_MAX_EMPLOYEES = 200;

export const missedCallsResponseSchema = callStatsMissedSchema.extend({
  /** By person (`key`), most missed first; at most `MISSED_CALLS_MAX_EMPLOYEES` rows. */
  byEmployee: z.array(missedCallsEmployeeSchema).max(MISSED_CALLS_MAX_EMPLOYEES),
  /** More employees than rows. The counters above still count every record. */
  truncated: z.boolean(),
  /** The viewer's access does not open the table (`team.scores`): `byEmployee`
   *  is empty by access, not because nobody missed a call. */
  byEmployeeHidden: z.boolean(),
  /** The projects the counters were narrowed to — the query's, kept to what the
   *  caller may see; `null` — not narrowed. Optional for the rollback, and it
   *  is how the page tells an edge that ignored `projectIds` (an older one)
   *  from one that applied it: the page shows an error, not org-wide numbers
   *  under a project filter. */
  projectIds: z.array(z.string()).nullable().optional(),
});

// ============================================================================
// GET /analytics/team-metrics
// ============================================================================

export const teamMetricsQuerySchema = z.object({
  period: z.enum(['today', 'week', 'month', 'quarter']).optional().default('week'),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  /** @deprecated legacy single-project filter — see callStatsQuerySchema's note. */
  projectId: z.string().uuid().optional(),
  /** Multi-project filter (project-multiselect K5) — wins over `projectId`
   *  whenever present and non-empty; the two are never merged. */
  projectIds: projectIdsFilterSchema,
});

// ============================================================================
// GET /analytics/config-events
// ============================================================================

/**
 * The query of `/config-events`: the quality-trend one plus the viewer's zone and
 * the optional «Голос клиента» kinds (#2394). An unknown zone name falls back to
 * `tzOffset`, as on the dashboards.
 */
export const configEventsQuerySchema = teamMetricsQuerySchema.extend({
  timeZone: z.string().max(64).optional(),
  include: z.enum(['voice']).optional(),
});

export const teamMetricsCategorySchema = z.object({
  category: z.string(),
  score: z.number(),
  benchmark: z.number(),
  agentCount: z.number().int().nonnegative(),
  trend: z.enum(['up', 'down', 'stable']),
  trendValue: z.number(),
});

export const teamMetricsPeriodSchema = z.object({
  start: z.string(),
  end: z.string(),
  recordCount: z.number().int().nonnegative(),
});

export const teamMetricsResponseSchema = z.object({
  categories: z.array(teamMetricsCategorySchema),
  /** 0-100; a placeholder 0 when `teamAverageScored` is false. */
  teamAverage: z.number(),
  /**
   * `false` — the team has no score: no category has data, or the selection's
   * projects have weights yet no call of it has a weighted score (every
   * criterion it carries weighs 0, #874 D4). A flag beside a number rather
   * than a nullable `teamAverage`: a tab running an older bundle keeps parsing
   * the response. Absent (an older edge) — scored.
   */
  teamAverageScored: z.boolean().optional(),
  benchmark: z.number(),
  weakAreas: z.array(z.string()),
  callsAnalyzed: z.number().int().nonnegative(),
  agentCount: z.number().int().nonnegative(),
  period: teamMetricsPeriodSchema,
});

// ============================================================================
// GET /analytics/quality-distribution
// ============================================================================

export const qualityDistributionQuerySchema = z.object({
  period: z.enum(['today', 'week', 'month', 'quarter']).optional().default('week'),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  /** @deprecated legacy single-project filter — see callStatsQuerySchema's note. */
  projectId: z.string().uuid().optional(),
  /** Multi-project filter (project-multiselect K5) — wins over `projectId`
   *  whenever present and non-empty; the two are never merged. */
  projectIds: projectIdsFilterSchema,
});

export const qualityDistributionBucketSchema = z.object({
  range: z.string(),
  count: z.number().int().nonnegative(),
  percentage: z.number(),
});

export const qualityDistributionResponseSchema = z.object({
  distribution: z.array(qualityDistributionBucketSchema),
  totalAgents: z.number().int().nonnegative(),
});

// ============================================================================
// GET /analytics/call-series-funnel
// ============================================================================

export const seriesFunnelQuerySchema = z.object({
  period: analyticsPeriodSchema,
  entityType: z.enum(['deal', 'lead']).optional(),
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  // Not .uuid() — demo-org project ids aren't UUIDs (see dashboard route's
  // raw searchParams.get('projectId'), which is similarly unconstrained).
  /** @deprecated legacy single-project filter — see callStatsQuerySchema's note. */
  projectId: z.string().optional(),
  /** Multi-project filter (project-multiselect K5) — wins over `projectId`
   *  whenever present and non-empty; the two are never merged. */
  projectIds: projectIdsFilterSchema,
  // Demo calls form series of their own since #1499 — the dashboard's filter.
  includeDemo: z.enum(['yes', 'no']).default('yes'),
});

export const seriesFunnelStepSchema = z.object({
  /** First rank covered by this step (the step's identity and sort key). */
  seriesNumber: z.number().int().positive(),
  /**
   * Last rank covered by this step. Ranks 1-5 are individual steps, so this
   * equals `seriesNumber`; past that the backend buckets ranks (6-10, 11-50)
   * to keep a long series from rendering hundreds of rows, and the open-ended
   * tail ("51+") sends null because it has no upper rank to name.
   * Optional so a response without it still parses as a single rank — this
   * schema rejects the WHOLE funnel on a miss (see the dropOffRate note).
   */
  seriesNumberTo: z.number().int().positive().nullable().optional(),
  /**
   * Series that reached this step — NOT calls inside it. For a bucket the two
   * differ: one series occupies several ranks, so counting calls would make a
   * bucket outgrow the step before it and the funnel would rise.
   */
  count: z.number().int().nonnegative(),
  // Signed, NOT nonnegative. Ranks are computed over a series' whole history
  // while counts are filtered to the period (see getCallSeriesFunnel), so a
  // later rank can hold MORE in-period calls than the one before it — an org
  // with long series (50+ calls) hits this on every period. A `.nonnegative()`
  // here made Zod reject the whole response client-side, and the Conversion
  // tab's isolated `.catch()` turned that into a permanently empty funnel.
  // The UI only renders the badge when `dropOffRate > 0`, so a negative value
  // (net inflow at that step) simply shows no drop-off label.
  dropOffRate: z.number(),
  percentage: z.number().nonnegative(),
});

export const seriesFunnelEntityTypeSchema = z.object({
  entityType: z.string(),
  steps: z.array(seriesFunnelStepSchema),
});

export const seriesFunnelResponseSchema = z.object({
  period: analyticsPeriodSchema,
  steps: z.array(seriesFunnelStepSchema),
  byEntityType: z.array(seriesFunnelEntityTypeSchema),
  totalSeries: z.number().int().nonnegative(),
  avgCallsPerSeries: z.number().nonnegative(),
});

// ============================================================================
// GET /analytics/outcome-funnel (Phase C, C-5)
//
// "Quality x conversion" funnel: Series started -> Deal open -> Won / Lost,
// plus avg canonical quality split by outcome and a stage x quality
// breakdown. v1 is deals only (no entityType — call_series_outcomes only
// ever writes seriesEntityType='deal', see migration 062).
//
// Contract carries RAW COUNTS + nullable averages, never a percentage — the
// UI derives conversion%/advancedShare% and gates them behind a minimum
// sample size (n<20 hides percentage labels; see ConversionTab). Averages
// are `.nullable()` with no min/max bound and dropOffRate's documented
// lesson applies here too: a speculative constraint that turns out wrong
// rejects the WHOLE response client-side, not just the one field.
// ============================================================================

export const outcomeFunnelQuerySchema = z.object({
  period: analyticsPeriodSchema,
  periodStart: z.string().datetime({ offset: true }).optional(),
  periodEnd: z.string().datetime({ offset: true }).optional(),
  // Not .uuid() — mirrors seriesFunnelQuerySchema (demo-org project ids aren't UUIDs).
  /** @deprecated legacy single-project filter — see callStatsQuerySchema's note. */
  projectId: z.string().optional(),
  /** Multi-project filter (project-multiselect K5) — wins over `projectId`
   *  whenever present and non-empty; the two are never merged. */
  projectIds: projectIdsFilterSchema,
  // Demo records are deals of their own since #1499 — the dashboard's filter.
  includeDemo: z.enum(['yes', 'no']).default('yes'),
});

export const outcomeFunnelStageBreakdownItemSchema = z.object({
  // The CRM install the stage belongs to: stage ids are portal-local, so a row is
  // named from the list of its own install (`GET /crm/stage-names`, #2872) and a
  // replaced portal's stage never takes the new portal's name. Absent — an older
  // server; null — the records carry no install. Neither is named.
  installationId: z.string().nullable().optional(),
  stageId: z.string(),
  records: z.number().int().nonnegative(),
  // Records the outcome-sync cron has actually evaluated for progression
  // (crmProgressionEvaluatedAt IS NOT NULL) — the denominator of advancedShare
  // AND the per-stage sample-size gate the UI applies before showing that
  // share at all (a one-record bucket must not read as "100%"; see
  // ConversionTab's STAGE_PERCENT_MIN_EVALUATED).
  evaluatedCount: z.number().int().nonnegative(),
  avgQuality: z.number().nullable(),
  // A true proportion (advanced / evaluated) — bounded by its own definition,
  // unlike dropOffRate. Null when the evaluated-count denominator is 0.
  advancedShare: z.number().min(0).max(1).nullable(),
});

export const outcomeFunnelResponseSchema = z.object({
  period: analyticsPeriodSchema,
  seriesStarted: z.number().int().nonnegative(),
  withOutcome: z.number().int().nonnegative(),
  won: z.number().int().nonnegative(),
  lost: z.number().int().nonnegative(),
  stillOpen: z.number().int().nonnegative(),
  avgQualityWon: z.number().nullable(),
  avgQualityLost: z.number().nullable(),
  stageBreakdown: z.array(outcomeFunnelStageBreakdownItemSchema),
  // MIN(lastSyncedAt) over the org's call_series_outcomes rows, unfiltered by
  // the requested window — null when the org has no synced outcome rows at
  // all (outcome-sync cron never ran / stageAwareScoring off for this org).
  syncSince: z.string().nullable(),
});

// ============================================================================
// Types
// ============================================================================

export type AnalyticsPeriod = z.infer<typeof analyticsPeriodSchema>;
export type CallStatsQuery = z.infer<typeof callStatsQuerySchema>;
export type CallStatsDirectionItem = z.infer<typeof callStatsDirectionItemSchema>;
export type CallStatsHourItem = z.infer<typeof callStatsHourItemSchema>;
export type CallStatsDurationBinItem = z.infer<typeof callStatsDurationBinItemSchema>;
export type CallStatsAgentWorkTimeItem = z.infer<typeof callStatsAgentWorkTimeItemSchema>;
export type CallStatsProjectItem = z.infer<typeof callStatsProjectItemSchema>;
export type CallStatsDailyTrendItem = z.infer<typeof callStatsDailyTrendItemSchema>;
export type CallStatsResponse = z.infer<typeof callStatsResponseSchema>;
export type CallStatsMissed = z.infer<typeof callStatsMissedSchema>;
export type MissedCallsQuery = z.infer<typeof missedCallsQuerySchema>;
export type MissedCallsEmployee = z.infer<typeof missedCallsEmployeeSchema>;
export type MissedCallsResponse = z.infer<typeof missedCallsResponseSchema>;
export type SeriesFunnelQuery = z.infer<typeof seriesFunnelQuerySchema>;
export type SeriesFunnelStep = z.infer<typeof seriesFunnelStepSchema>;
export type SeriesFunnelEntityType = z.infer<typeof seriesFunnelEntityTypeSchema>;
export type SeriesFunnelResponse = z.infer<typeof seriesFunnelResponseSchema>;
export type TeamMetricsQuery = z.infer<typeof teamMetricsQuerySchema>;
export type TeamMetricsCategoryItem = z.infer<typeof teamMetricsCategorySchema>;
export type TeamMetricsPeriod = z.infer<typeof teamMetricsPeriodSchema>;
export type TeamMetricsResponse = z.infer<typeof teamMetricsResponseSchema>;
export type QualityDistributionQuery = z.infer<typeof qualityDistributionQuerySchema>;
export type QualityDistributionBucket = z.infer<typeof qualityDistributionBucketSchema>;
export type QualityDistributionResponse = z.infer<
  typeof qualityDistributionResponseSchema
>;
export type OutcomeFunnelQuery = z.infer<typeof outcomeFunnelQuerySchema>;
export type OutcomeFunnelStageBreakdownItem = z.infer<
  typeof outcomeFunnelStageBreakdownItemSchema
>;
export type OutcomeFunnelResponse = z.infer<typeof outcomeFunnelResponseSchema>;
